import type { IMasterRecord } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

export type MasterInput = Pick<IMasterRecord, 'name' | 'description'>;

// The API always echoes back an `isActive` flag (derived server-side from `deletedAt`) on every
// master response — a read-only, response-only concern, not part of `IMasterRecord`'s
// create/update input shape, so it's layered on here rather than added to the shared type.
export interface IMasterRecordWithStatus extends IMasterRecord {
  isActive: boolean;
}

// Generic over a base path (e.g. /api/v1/account-tiers) — all four account masters share this
// exact CRUD shape, so one parameterized client serves all of them instead of four near-
// identical copies.
export async function fetchMasterList(basePath: string): Promise<IMasterRecordWithStatus[]> {
  const res = await apiFetch(basePath);
  if (!res.ok) throw new Error(`Failed to fetch ${basePath}: ${res.status}`);
  return res.json() as Promise<IMasterRecordWithStatus[]>;
}

// Active-only counterpart of fetchMasterList() — the shared data source for every picker that
// offers a master record as a *new* selection (see each master controller's GET /lookup route).
// Reach for this in any dropdown; fetchMasterList() stays reserved for the admin CRUD pages,
// which must keep showing inactive records so they can be reactivated.
export async function fetchMasterLookup(basePath: string): Promise<IMasterRecordWithStatus[]> {
  const res = await apiFetch(`${basePath}/lookup`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch ${basePath}: ${res.status}`));
  return res.json() as Promise<IMasterRecordWithStatus[]>;
}

export async function createMaster(basePath: string, data: MasterInput): Promise<IMasterRecordWithStatus> {
  const res = await apiFetch(basePath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Create failed'));
  return res.json() as Promise<IMasterRecordWithStatus>;
}

export async function updateMaster(
  basePath: string,
  id: string,
  data: Partial<MasterInput>,
): Promise<IMasterRecordWithStatus> {
  const res = await apiFetch(`${basePath}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Update failed'));
  return res.json() as Promise<IMasterRecordWithStatus>;
}

export async function deactivateMaster(basePath: string, id: string): Promise<IMasterRecordWithStatus> {
  const res = await apiFetch(`${basePath}/${id}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Deactivate failed'));
  return res.json() as Promise<IMasterRecordWithStatus>;
}

export async function activateMaster(basePath: string, id: string): Promise<IMasterRecordWithStatus> {
  const res = await apiFetch(`${basePath}/${id}/activate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Activate failed'));
  return res.json() as Promise<IMasterRecordWithStatus>;
}
